<script setup>
/* eslint-disable */
import { computed, ref } from 'vue'
import { useGameStore } from '@/store/game'
import { AuctionManager } from '@/services/auctionManager'
import PersonalBest from './PersonalBest.vue'
import { useDarkModeStore } from '@/store/darkMode'
import { applyThemeClasses } from '@/utils/themeColors'
import { getChocoboColorClass, getBehaviorClass, getTrainingClass } from '@/utils/colorUtils'


const props = defineProps({
  chocobo: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close'])
const gameStore = useGameStore()

// Rename functionality
const isEditing = ref(false)
const newName = ref('')
const showError = ref(false)
const errorMessage = ref('')

const startEditing = () => {
  newName.value = props.chocobo.name
  isEditing.value = true
  showError.value = false
}

const saveNewName = () => {
  // Validate name
  if (!newName.value.trim()) {
    showError.value = true
    errorMessage.value = 'Name cannot be empty'
    return
  }
  
  if (newName.value.length > 20) {
    showError.value = true
    errorMessage.value = 'Name must be 20 characters or less'
    return
  }
  
  // Update chocobo name
  props.chocobo.name = newName.value.trim()
  isEditing.value = false
  showError.value = false
  
  // Save game state to persist the change
  gameStore.saveGame()
}

const cancelEditing = () => {
  isEditing.value = false
  showError.value = false
}

const getRegionName = (region) => {
  const regions = {
    'region1': 'Eastern Metropolis',
    'region2': 'Junon Area',
    'region3': 'Western Seaboard',
    'region4': 'Central Range',
    'region5': 'Mideel Forest',
    'region6': 'Northern Territory',
    'region7': 'Stable born'
  }
  return regions[region] || region
}

const totalGains = computed(() => props.chocobo.getTotalGains())

const getRaceHistory = computed(() => {
  const totalRaces = props.chocobo.raceHistory?.length || 0
  const wins = props.chocobo.raceHistory?.filter(r => r.position === 1).length || 0
  const places = props.chocobo.raceHistory?.filter(r => r.position <= 2).length || 0
  const shows = props.chocobo.raceHistory?.filter(r => r.position <= 3).length || 0

  return {
    totalRaces,
    wins,
    places,
    shows,
    winRate: totalRaces ? ((wins / totalRaces) * 100).toFixed(1) : 0,
    placeRate: totalRaces ? ((places / totalRaces) * 100).toFixed(1) : 0,
    showRate: totalRaces ? ((shows / totalRaces) * 100).toFixed(1) : 0
  }
})

const marketValue = computed(() => {
  return AuctionManager.calculateBaseValue(props.chocobo)
})

const getStatProgress = (current, base, bonus) => {
  const progress = ((current - base) / (base * 0.5)) * 100
  return Math.min(100, Math.max(0, progress))
}

const personalBests = computed(() => {
  return props.chocobo?.personalBests || {};
});

const updateSalePreferences = () => {
  // Save game state to persist the change
  gameStore.saveGame()
}
</script>

<template>
  <div :class="`fixed inset-0 ${applyThemeClasses('components.modal.overlay')} flex items-center justify-center z-50`">
    <div :class="`rounded-lg ${applyThemeClasses('components.modal.container')} max-h-[90vh] overflow-y-auto p-6`">
      <!-- Header -->
      <div class="flex justify-between items-center p-3 mb-6">
        <div class="flex items-center gap-2">
          <div v-if="!isEditing" class="flex items-center gap-2">
            <h2 :class="applyThemeClasses('text.primary')">{{ chocobo.name }}</h2>
            <button
              @click="startEditing"
              class="p-1 text-gray-500 hover:text-gray-700"
              title="Rename Chocobo"
            >
              ✎
            </button>
          </div>
          <div v-else class="flex items-center gap-2">
            <input
              v-model="newName"
              type="text"
              class="px-2 py-1 border rounded text-xl font-bold"
              :class="{ 'border-red-500': showError }"
              @keyup.enter="saveNewName"
              @keyup.esc="cancelEditing"
              ref="nameInput"
            >
            <div class="flex gap-1">
              <button
                @click="saveNewName"
                class="p-1 text-green-600 hover:text-green-700"
                title="Save"
              >
                ✓
              </button>
              <button
                @click="cancelEditing"
                class="p-1 text-red-600 hover:text-red-700"
                title="Cancel"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
        <button 
          @click="$emit('close')"
          class="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>

      <!-- Error Message -->
      <div 
        v-if="showError" 
        class="mx-6 mb-4 p-2 bg-red-100 text-red-800 rounded text-sm"
      >
        {{ errorMessage }}
      </div>

      <!-- Content -->
      <div class="p-6 space-y-6">
        <!-- Basic Info -->
        <div class="grid grid-cols-2 gap-6">
          <div>
            <h3 :class="applyThemeClasses('text.primaryLow')">Basic Information</h3>
            <div class="space-y-2">
              <div>
                <span :class="applyThemeClasses('text.secondary')">Region: </span> 
                <span :class="applyThemeClasses('text.tertiary')">{{ getRegionName(chocobo.region) }}</span>
              </div>
              <div>
                <span :class="applyThemeClasses('text.secondary')">Age: </span> 
                <span :class="applyThemeClasses('text.tertiary')">{{ chocobo.getFormattedAge(gameStore.currentDay) }}</span>
                <span :class="applyThemeClasses('text.secondary')" class="ml-2">({{ chocobo.getAgeCategory(gameStore.currentDay) }})</span>
              </div>
              <div>
                <span :class="applyThemeClasses('text.secondary')">Experience: </span> 
                <span :class="applyThemeClasses('text.tertiary')" v-if="chocobo.level < chocobo.maxLevel">{{ chocobo.getXpToNextLevel() }} XP to next level</span>
                <span :class="applyThemeClasses('text.tertiary')" v-else>Max Level Reached</span>
              </div>
              <div>
                <span :class="applyThemeClasses('text.secondary')">Market Value: </span> 
                <span :class="applyThemeClasses('text.tertiary')">{{ marketValue.toLocaleString() }}G</span>
              </div>
              <div 
                :class="[getBehaviorClass(chocobo.behaviorId), 'inline-block px-2 py-1 rounded-full text-sm mt-1']"
              >
                {{ chocobo.behavior.description }}
              </div>
            </div>
          </div>

          <!-- Special Traits -->
          <div>
            <h3 :class="applyThemeClasses('text.primaryLow')">Special Traits</h3>
            <div class="space-y-2">
              <div 
                v-if="chocobo.hasSpeedXFactor"
                class="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-sm inline-block"
              >
                Speed X-Factor
              </div>
              <div 
                v-if="chocobo.hasStaminaXFactor"
                class="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-sm inline-block"
              >
                Stamina X-Factor
              </div>
            </div>
          </div>
        </div>

        <!-- Sale Preferences -->
        <div>
          <h3 :class="applyThemeClasses('text.primaryLow')">Sale Preferences</h3>
          <div class="space-y-3">
            <label class="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                v-model="chocobo.notForSale"
                @change="updateSalePreferences"
                class="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
              >
              <div>
                <span :class="`font-medium ${applyThemeClasses('text.secondary')}`">
                  Not for Sale
                </span>
                <div :class="`text-sm ${applyThemeClasses('text.tertiary')}`">
                  Prevent other owners from making offers on this chocobo
                </div>
              </div>
            </label>
            
            <div v-if="chocobo.notForSale" class="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
              <div class="flex items-center">
                <span class="text-yellow-600 text-sm mr-2">🚫</span>
                <div class="text-sm">
                  <p class="font-medium text-yellow-800">This chocobo is marked as not for sale</p>
                  <p class="text-yellow-700">Other owners will not make any bidding offers</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Lineage -->
        <div>
          <h3 :class="applyThemeClasses('text.primaryLow')">Lineage</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Mother -->
            <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')} p-4`">
              <div class="flex items-center space-x-3 mb-3">
                <div class="text-pink-500 text-2xl">♀</div>
                <div>
                  <h4 :class="`font-semibold ${applyThemeClasses('text.secondary')}`">Mother</h4>
                </div>
              </div>
              <div v-if="chocobo.motherName" class="space-y-2">
                <div class="flex items-center space-x-2">
                  <div :class="`w-4 h-4 rounded ${getChocoboColorClass(chocobo.motherColor)}`"></div>
                  <span :class="applyThemeClasses('text.secondaryBold')">{{ chocobo.motherName }}</span>
                </div>
                <div class="text-sm space-y-1 text-gray-600">
                  <div class="flex justify-between">
                    <span>Level:</span>
                    <span class="font-medium">{{ chocobo.motherStats?.level || 'Unknown' }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>Speed:</span>
                    <span class="font-medium">{{ chocobo.motherStats?.speed || 'Unknown' }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>Stamina:</span>
                    <span class="font-medium">{{ chocobo.motherStats?.stamina || 'Unknown' }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>Wins:</span>
                    <span class="font-medium text-green-600">{{ chocobo.motherStats?.wins || 0 }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>Earnings:</span>
                    <span class="font-medium text-blue-600">{{ (chocobo.motherStats?.earnings || 0).toLocaleString() }}G</span>
                  </div>
                </div>
              </div>
              <div v-else class="text-center text-gray-500 py-4">
                <div class="text-gray-400 mb-2">🤷</div>
                <div class="text-sm">Unknown</div>
              </div>
            </div>

            <!-- Father -->
            <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')} p-4`">
              <div class="flex items-center space-x-3 mb-3">
                <div class="text-blue-500 text-2xl">♂</div>
                <div>
                  <h4 :class="`font-semibold ${applyThemeClasses('text.secondary')}`">Father</h4>
                </div>
              </div>
              <div v-if="chocobo.fatherName" class="space-y-2">
                <div class="flex items-center space-x-2">
                  <div :class="`w-4 h-4 rounded ${getChocoboColorClass(chocobo.fatherColor)}`"></div>
                  <span :class="applyThemeClasses('text.secondaryBold')">{{ chocobo.fatherName }}</span>
                </div>
                <div class="text-sm space-y-1 text-gray-600">
                  <div class="flex justify-between">
                    <span>Level:</span>
                    <span class="font-medium">{{ chocobo.fatherStats?.level || 'Unknown' }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>Speed:</span>
                    <span class="font-medium">{{ chocobo.fatherStats?.speed || 'Unknown' }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>Stamina:</span>
                    <span class="font-medium">{{ chocobo.fatherStats?.stamina || 'Unknown' }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>Wins:</span>
                    <span class="font-medium text-green-600">{{ chocobo.fatherStats?.wins || 0 }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>Earnings:</span>
                    <span class="font-medium text-blue-600">{{ (chocobo.fatherStats?.earnings || 0).toLocaleString() }}G</span>
                  </div>
                </div>
              </div>
              <div v-else class="text-center text-gray-500 py-4">
                <div class="text-gray-400 mb-2">🤷</div>
                <div class="text-sm">Unknown</div>
              </div>
            </div>
          </div>
          
          <!-- Lineage Summary -->
          <div class="mt-4 p-3 bg-gray-50 rounded-lg">
            <div class="text-sm text-gray-600 text-center">
              <span v-if="chocobo.motherName && chocobo.fatherName" class="italic">
                "{{ chocobo.name }} is the offspring of {{ chocobo.motherName }} and {{ chocobo.fatherName }}"
              </span>
              <span v-else-if="chocobo.motherName" class="italic">
                "{{ chocobo.name }} is the offspring of {{ chocobo.motherName }} and an unknown sire"
              </span>
              <span v-else-if="chocobo.fatherName" class="italic">
                "{{ chocobo.name }} is the offspring of an unknown hen and {{ chocobo.fatherName }}"
              </span>
              <span v-else class="italic text-gray-500">
                "{{ chocobo.name }} is a wild-caught chocobo - we don't know their lineage"
              </span>
            </div>
          </div>
        </div>

        <!-- Stats -->
        <div>
          <h3 :class="applyThemeClasses('text.primaryLow')">Stats & Improvements</h3>
          <div class="grid grid-cols-2 gap-6">
            <!-- Speed -->
            <div>
              <div class="flex justify-between mb-1">
                <div class="text-sm font-medium">
                  <span :class="applyThemeClasses('text.secondary')">Speed</span>

                </div>
                <div class="text-sm text-gray-600">
                  <span :class="applyThemeClasses('text.tertiary')">{{ chocobo.speed }}</span>
                  <span class="text-green-600" v-if="totalGains.speed > 0">
                    (+{{ totalGains.speed }})
                  </span>
                  <div class="text-xs text-gray-500" v-if="chocobo.getEffectiveSpeed(gameStore.currentDay) !== chocobo.speed">
                    Effective: {{ chocobo.getEffectiveSpeed(gameStore.currentDay) }} (age adjusted)
                  </div>
                </div>
              </div>
              <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  class="h-full bg-blue-500 transition-all"
                  :style="{ width: `${getStatProgress(chocobo.speed, chocobo.baseSpeed, totalGains.speed)}%` }"
                ></div>
              </div>
            </div>

            <!-- Stamina -->
            <div>
              <div class="flex justify-between mb-1">
                <div class="text-sm font-medium">
                  <span :class="applyThemeClasses('text.secondary')">Stamina</span>
                </div>
                <div class="text-sm text-gray-600">
                  <span :class="applyThemeClasses('text.tertiary')">{{ chocobo.stamina }}</span>
                  <span class="text-green-600" v-if="totalGains.stamina > 0">
                    (+{{ totalGains.stamina }})
                  </span>
                  <div class="text-xs text-gray-500" v-if="chocobo.getEffectiveStamina(gameStore.currentDay) !== chocobo.stamina">
                    Effective: {{ chocobo.getEffectiveStamina(gameStore.currentDay) }} (age adjusted)
                  </div>
                </div>
              </div>
              <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  class="h-full bg-green-500 transition-all"
                  :style="{ width: `${getStatProgress(chocobo.stamina, chocobo.baseStamina, totalGains.stamina)}%` }"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Race History -->
        <div>
          <h3 :class="applyThemeClasses('text.primaryLow')">Race History</h3>
          <div class="grid grid-cols-3 gap-4">
            <!-- Win Rate -->
            <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')} p-4`">
              <div class="text-yellow-700 font-medium">Win Rate</div>
              <div class="text-2xl font-bold text-yellow-600">
                {{ getRaceHistory.winRate }}%
              </div>
              <div class="text-sm text-yellow-700">
                {{ getRaceHistory.wins }} First in {{ getRaceHistory.totalRaces }} races
              </div>
            </div>

            <!-- Place Rate -->
            <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')} p-4`">
              <div class="text-gray-400 font-medium">2nd+ Rate</div>
              <div class="text-2xl font-bold text-gray-400">
                {{ getRaceHistory.placeRate }}%
              </div>
              <div class="text-sm text-gray-400">
                {{ getRaceHistory.places }} Second or better in {{ getRaceHistory.totalRaces }} races
              </div>
            </div>

            <!-- Show Rate -->
            <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')} p-4`">
              <div class="text-amber-700 font-medium">3rd+ Rate</div>
              <div class="text-2xl font-bold text-amber-600">
                {{ getRaceHistory.showRate }}%
              </div>
              <div class="text-sm text-amber-700">
                {{ getRaceHistory.shows }} Third or better in {{ getRaceHistory.totalRaces }} races
              </div>
            </div>
          </div>
        </div>
        <!-- Racing Times Section -->
        <div class="p-6">
          <h3 :class="applyThemeClasses('text.primaryLow')">Racing Times</h3>
          <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')} p-3`">
            <PersonalBest
              :personal-bests="personalBests"
            />
          </div>
        </div>

        <!-- Training Focus -->
        <div>
          <h3 :class="applyThemeClasses('text.primaryLow')">Current Training</h3>
          <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')} p-4`">
            <div class="flex items-center justify-between">
              <div>
                <div class="font-medium"><span :class="applyThemeClasses('text.primaryLow')">{{ chocobo.trainingType.charAt(0).toUpperCase() + chocobo.trainingType.slice(1) }} Focus</span></div>
                <div class="text-sm text-gray-600">
                  <span :class="applyThemeClasses('text.secondary')">{{ chocobo.trainingType === 'behavior' ? 
                    `Progress: ${chocobo.consecutiveBehaviorTraining}/6 levels` : 
                    `Focusing on ${chocobo.trainingType} improvement` 
                  }}</span>
                </div>
                <div class="text-xs text-gray-500 mt-1">
                  Training effectiveness: {{ Math.round(chocobo.getAgeTrainingModifier(gameStore.currentDay) * 100) }}% (age factor)
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="border-t p-4 flex justify-end">
        <button 
          @click="$emit('close')"
          class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</template>